import { loginUser } from 'app/auth/actions';
import { showErrorMessageSaga } from 'app/messages/saga';
import type { SagaIterator } from 'redux-saga';
import { all, call, put, race, take, takeLatest } from 'redux-saga/effects';
import getLoginRedirect from '../common/utils/get-login-redirect.js';
import { getStorageChannel } from '../common/utils/local-storage-saga.js';
import { getRoutineSaga } from '../common/utils/new-sagas.js';
import { ssoLoginRoutine } from './actions.js';
import { getSSOAuthorizeUrl, ssoLogin } from './api.js';

export const OAUTH_CODE_KEY = 'oauth-code';
export const OAUTH_ERROR_KEY = 'oauth-error';

export function* ssoLoginSaga(payload: string): SagaIterator {
  const { data } = yield call(getSSOAuthorizeUrl, payload);
  yield call([window, 'open'], data.authorizeUrl);

  const storageChannel = yield call(getStorageChannel, OAUTH_CODE_KEY);
  const errorChannel = yield call(getStorageChannel, OAUTH_ERROR_KEY);

  const { storageEvent, errorEvent } = yield race({
    storageEvent: take(storageChannel),
    errorEvent: take(errorChannel),
    cancelEvent: take(ssoLoginRoutine.cancelled)
  });

  if (errorEvent) {
    const errorPayload = localStorage.getItem(OAUTH_ERROR_KEY);
    yield put(ssoLoginRoutine.rejected(decodeURI(errorPayload ?? 'A problem occurred while logging in.')));
  }

  if (storageEvent) {
    const loginPayload = localStorage.getItem(OAUTH_CODE_KEY);
    const { code, provider } = JSON.parse(loginPayload ?? '');
    const { data } = yield call(ssoLogin, code, provider);
    yield put(loginUser.fulfilled({ data }, { redirectOnSuccess: getLoginRedirect() }));
    yield put(ssoLoginRoutine.fulfilled());
  }

  storageChannel.close();
  errorChannel.close();
}

export default function* root(): SagaIterator {
  yield all([
    takeLatest(ssoLoginRoutine.REQUESTED, getRoutineSaga(ssoLoginRoutine, ssoLoginSaga)),
    takeLatest(ssoLoginRoutine.rejected, showErrorMessageSaga)
  ]);
}
